/**
 * How a team's member count reads, and how long the list waits to ask again.
 *
 * A count-only team ("End Users", everyone) has its members counted in the
 * background by the server, never listed (2 Oct): counting 1,743 members takes
 * ~15 s, so the first read answers `member_count: null` with
 * `member_count_pending: true` and the page asks again until the count lands.
 *
 * Kept import-free (types only) so `node --test` can load it from source.
 */

/** Structural twin of the generated `TeamSummary`, narrowed to the count. */
export interface TeamCountLike {
  member_count?: number | null;
  member_count_pending?: boolean;
}

/** How often a page re-reads while a count is pending. The count takes ~15 s. */
export const COUNT_REFETCH_MS = 20_000;

export type MemberCountState = { kind: "known"; count: number } | { kind: "pending" };

/** Known count, or pending. A missing count that is not pending reads as 0. */
export function memberCountState(team: TeamCountLike): MemberCountState {
  if (team.member_count_pending === true) {
    return { kind: "pending" };
  }
  return { kind: "known", count: team.member_count ?? 0 };
}

/** TanStack Query `refetchInterval`: poll while any team's count is pending, else stop. */
export function countRefetchInterval(teams: readonly TeamCountLike[] | undefined): number | false {
  return (teams ?? []).some((team) => team.member_count_pending === true)
    ? COUNT_REFETCH_MS
    : false;
}
